import React from 'react';
import { useDarkMode } from '../../../../contexts/DarkModeContext';
import {
  TRIGGER_PRAYERS,
  formatClockMinutes,
  parseClockMinutes,
} from '../../../../utils/screenSchedule';

/**
 * One schedule moment: a clock time, or a prayer ± minutes. Shape matches the clock's
 * trigger map ({type: 'CLOCK_TIME'|'PRAYER', prayer?, offsetMinutes, clockTimeMinutes?}).
 * Parameterised by `label` so any schedule row (From / Until, Dim at / Restore at) can use it.
 */
export const TriggerPicker = ({ label, trigger, onChange, disabled, strings, idPrefix }) => {
  const { isDarkMode } = useDarkMode();
  const inputClass = `rounded-lg border px-3 py-2 text-sm ${
    isDarkMode ? 'bg-[#1A1A1A] border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'
  } disabled:cursor-not-allowed`;
  const segment = (active) => `px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${
    active
      ? isDarkMode ? 'bg-emerald-500 text-black' : 'bg-emerald-600 text-white'
      : isDarkMode ? 'bg-[#1A1A1A] text-gray-400' : 'bg-gray-100 text-gray-600'
  } disabled:cursor-not-allowed`;

  const isPrayer = trigger.type === 'PRAYER';

  const setType = (type) => {
    if (type === trigger.type) return;
    onChange(type === 'PRAYER'
      ? { type: 'PRAYER', prayer: 'Isha', offsetMinutes: 0 }
      : { type: 'CLOCK_TIME', offsetMinutes: 0, clockTimeMinutes: trigger.clockTimeMinutes ?? 0 });
  };

  return (
    <div className={`py-3 ${disabled ? 'opacity-50' : ''}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="text-base font-medium">{label}</span>
        <div className="flex gap-2">
          <button type="button" disabled={disabled} className={segment(!isPrayer)} onClick={() => setType('CLOCK_TIME')}>
            {strings.clockTime}
          </button>
          <button type="button" disabled={disabled} className={segment(isPrayer)} onClick={() => setType('PRAYER')}>
            {strings.prayerTime}
          </button>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        {isPrayer ? (
          <>
            <select
              id={`${idPrefix}-prayer`}
              aria-label={`${label} – ${strings.prayerTime}`}
              disabled={disabled}
              className={inputClass}
              value={trigger.prayer}
              onChange={(e) => onChange({ ...trigger, prayer: e.target.value })}
            >
              {TRIGGER_PRAYERS.map((p) => (
                <option key={p} value={p}>{strings.prayers[p] || p}</option>
              ))}
            </select>
            <label className="flex items-center gap-2 text-sm" htmlFor={`${idPrefix}-offset`}>
              {strings.offset}
              <input
                id={`${idPrefix}-offset`}
                type="number"
                step={5}
                min={-180}
                max={180}
                disabled={disabled}
                className={`${inputClass} w-24`}
                value={trigger.offsetMinutes}
                onChange={(e) => {
                  // An empty field (or a lone "-" while typing) reads as '' — don't turn it into 0.
                  if (e.target.value === '') return;
                  const n = Math.round(Number(e.target.value));
                  if (!Number.isFinite(n)) return;
                  onChange({ ...trigger, offsetMinutes: Math.max(-180, Math.min(180, n)) });
                }}
              />
            </label>
          </>
        ) : (
          <input
            id={`${idPrefix}-time`}
            aria-label={`${label} – ${strings.clockTime}`}
            type="time"
            disabled={disabled}
            className={inputClass}
            value={formatClockMinutes(trigger.clockTimeMinutes)}
            onChange={(e) => {
              const minutes = parseClockMinutes(e.target.value);
              if (minutes !== null) onChange({ ...trigger, clockTimeMinutes: minutes });
            }}
          />
        )}
      </div>
    </div>
  );
};
